import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

// Interfaces for Metrics API
export interface Metric {
  id: number;
  name: string;
  description?: string;
  category: string;
  unit: string;
  is_active: boolean;
  configuration?: {
    metric: string;
    dimensions?: string[];
    threshold?: number;
    period?: 'day' | 'week';
    operator?: string;
    filters?: {
      date_range?: { start?: string; end?: string };
      habit_name?: string;
      habit_category?: string;
    };
  };
}

export interface MetricValue extends Metric {
  value: number;
}

export interface MetricFilters {
  category?: string;
  is_active?: boolean;
}

class MetricService {
  /**
   * Get all available metrics
   * @param filters Optional filters for category and is_active
   * @returns Promise with metrics list
   */
  async getAllMetrics(filters?: MetricFilters): Promise<{ 
    success: boolean; 
    metrics?: Metric[]; 
    count?: number;
    message?: string 
  }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
      
      const queryString = queryParams.toString();
      const url = `/metrics${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      throw error;
    }
  }

  /**
   * Get a specific metric by name
   * @param metricName The name of the metric
   * @returns Promise with metric details
   */
  async getMetricByName(metricName: string): Promise<{ 
    success: boolean; 
    metric?: Metric;
    message?: string 
  }> {
    try {
      const response = await makeAuthenticatedRequest(`/metrics/${metricName}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error(`Error fetching metric ${metricName}:`, error);
      throw error;
    }
  }

  /**
   * Calculate the value of a metric for a specific user
   * @param metricName The name of the metric to calculate
   * @param userId The user ID to calculate the metric for
   * @returns Promise with calculated metric value
   */
  async calculateMetric(metricName: string, userId: string | number): Promise<{ 
    success: boolean; 
    metric?: MetricValue;
    message?: string 
  }> {
    try {
      const response = await makeAuthenticatedRequest(
        `/metrics/${metricName}/calculate?user_id=${userId}`, 
        {
          method: 'GET'
        }
      );
      
      return response;
    } catch (error) {
      console.error(`Error calculating metric ${metricName} for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Calculate multiple metrics at once for a user
   * @param metricNames Array of metric names to calculate
   * @param userId The user ID to calculate metrics for
   * @returns Promise with array of calculated metric values
   */
  async calculateMultipleMetrics(metricNames: string[], userId: string | number): Promise<{
    success: boolean;
    metrics?: MetricValue[];
    errors?: { metricName: string; error: string }[];
  }> {
    try {
      const results = await Promise.allSettled(
        metricNames.map(metricName => this.calculateMetric(metricName, userId))
      );

      const metrics: MetricValue[] = [];
      const errors: { metricName: string; error: string }[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success && result.value.metric) {
          metrics.push(result.value.metric);
        } else if (result.status === 'rejected') {
          errors.push({
            metricName: metricNames[index],
            error: result.reason?.message || 'Unknown error'
          });
        }
      });

      return {
        success: true,
        metrics,
        errors: errors.length > 0 ? errors : undefined
      };
    } catch (error) {
      console.error('Error calculating multiple metrics:', error);
      throw error;
    }
  }

  /**
   * Get metrics by category
   * @param category The category to filter by
   * @returns Promise with metrics in the specified category
   */
  async getMetricsByCategory(category: string): Promise<{ 
    success: boolean; 
    metrics?: Metric[]; 
    count?: number;
    message?: string 
  }> {
    return this.getAllMetrics({ category, is_active: true });
  }

  /**
   * Get all available metric categories
   * @returns Array of unique metric categories
   */
  async getMetricCategories(): Promise<string[]> {
    try {
      const response = await this.getAllMetrics({ is_active: true });
      
      if (response.success && response.metrics) {
        const categories = new Set(response.metrics.map(m => m.category));
        return Array.from(categories).sort();
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching metric categories:', error);
      return [];
    }
  }

  /**
   * Format metric value with unit
   * @param value The metric value
   * @param unit The unit type
   * @returns Formatted string
   */
  formatMetricValue(value: number, unit: string): string {
    switch (unit) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value);
      
      case 'percentage':
        return `${value.toFixed(2)}%`;
      
      case 'count':
        return Math.floor(value).toLocaleString();
      
      case 'days':
        return `${Math.floor(value)} ${value === 1 ? 'day' : 'days'}`;
      
      case 'minutes':
        const hours = Math.floor(value / 60);
        const minutes = Math.floor(value % 60);
        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
      
      default:
        return value.toLocaleString();
    }
  }

  /**
   * Helper method to group metrics by category
   * @param metrics Array of metrics
   * @returns Object with metrics grouped by category
   */
  groupMetricsByCategory(metrics: Metric[]): { [category: string]: Metric[] } {
    return metrics.reduce((acc, metric) => {
      if (!acc[metric.category]) {
        acc[metric.category] = [];
      }
      acc[metric.category].push(metric);
      return acc;
    }, {} as { [category: string]: Metric[] });
  }

  /**
   * Build a custom metric query
   * @param payload The metric builder payload
   * @returns Promise with table data
   */
  async buildMetric(payload: {
    metric: string;
    dimensions?: string[];
    threshold?: number;
    period?: 'day' | 'week';
    filters?: {
      date_range?: { start?: string; end?: string };
      habit_name?: string;
      habit_category?: string;
    };
  }): Promise<{
    success: boolean;
    columns?: string[];
    rows?: any[][];
    message?: string;
  }> {
    try {
      const response = await makeAuthenticatedRequest('/metrics/build', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      return response;
    } catch (error) {
      console.error('Error building metric:', error);
      throw error;
    }
  }

  /**
   * Get available options for metric builder
   * @returns Promise with available metrics and dimensions
   */
  async getMetricBuilderOptions(): Promise<{
    success: boolean;
    metrics?: Array<{ id: string; name: string; description: string }>;
    dimensions?: Array<{ id: string; label: string }>;
    message?: string;
  }> {
    try {
      const response = await makeAuthenticatedRequest('/metrics/build/options', {
        method: 'GET',
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching metric builder options:', error);
      throw error;
    }
  }

  /**
   * Get filter values for metric builder
   * @param type The type of filter (e.g., 'habit_name', 'habit_category')
   * @returns Promise with filter values
   */
  async getFilterValues(type: string): Promise<{
    success: boolean;
    values?: string[];
    message?: string;
  }> {
    try {
      const response = await makeAuthenticatedRequest(
        `/metrics/build/filter-values?type=${encodeURIComponent(type)}`,
        {
          method: 'GET',
        }
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching filter values for ${type}:`, error);
      throw error;
    }
  }

  /**
   * Save a built metric to the database
   * @param payload The metric data to save
   * @returns Promise with save result
   */
  async saveMetric(payload: {
    name: string;
    description?: string;
    category: string;
    unit: string;
    metric: string;
    dimensions?: string[];
    threshold?: number;
    period?: 'day' | 'week';
    filters?: {
      date_range?: { start?: string; end?: string };
      habit_name?: string;
      habit_category?: string;
    };
  }): Promise<{
    success: boolean;
    message?: string;
    metric?: any;
  }> {
    try {
      const response = await makeAuthenticatedRequest('/metrics/build/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      return response;
    } catch (error) {
      console.error('Error saving metric:', error);
      throw error;
    }
  }

  /**
   * Update a metric by name
   * @param metricName The name of the metric to update
   * @param updates The fields to update
   * @returns Promise with update result
   */
  async updateMetric(metricName: string, updates: {
    name?: string;
    description?: string;
    category?: string;
    unit?: string;
    is_active?: boolean;
    configuration?: {
      metric?: string;
      dimensions?: string[];
      threshold?: number;
      period?: 'day' | 'week';
      operator?: string;
      filters?: {
        date_range?: { start?: string; end?: string };
        habit_name?: string;
        habit_category?: string;
      };
    };
  }): Promise<{
    success: boolean;
    message?: string;
    metric?: Metric;
  }> {
    try {
      const response = await makeAuthenticatedRequest(`/metrics/${metricName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      return response;
    } catch (error) {
      console.error(`Error updating metric ${metricName}:`, error);
      throw error;
    }
  }

  /**
   * Delete a metric by name
   * @param metricName The name of the metric to delete
   * @returns Promise with delete result
   */
  async deleteMetric(metricName: string): Promise<{
    success: boolean;
    message?: string;
  }> {
    try {
      const response = await makeAuthenticatedRequest(`/metrics/${metricName}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error(`Error deleting metric ${metricName}:`, error);
      throw error;
    }
  }
}

export const metricService = new MetricService();

